import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Card, CardContent } from "@/components/ui/card.tsx";

type MentorInfoCardProps = {
  mentorId: number;
  fullName: string;
  profileSrc?: string;
  title?: string;
  university?: string;
  careerFocus?: string;
  showProfileLink?: boolean;
  wrapInCard?: boolean;
  className?: string;
};

export default function MentorInfoCard({
  mentorId,
  fullName,
  profileSrc,
  title,
  university,
  careerFocus,
  showProfileLink = true,
  wrapInCard = true,
  className,
}: MentorInfoCardProps) {
  const { t } = useTranslation();

  const body = (
    <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center">
      <Avatar className="h-24 w-24 shrink-0 rounded-xl border-2 border-border sm:h-28 sm:w-28">
        <AvatarImage src={profileSrc} alt={fullName} className="object-cover" />
        <AvatarFallback className="rounded-xl bg-accent/15 text-xl font-semibold text-text-1">
          {fullName.charAt(0) || "M"}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <h2 className="text-xl font-semibold text-text-1">{fullName}</h2>
        {title && <p className="mt-1 text-base text-text-2">{title}</p>}
        {university && <p className="mt-1 text-sm text-text-2">{university}</p>}
        {careerFocus && (
          <span className="mt-2 inline-block rounded-full border border-border bg-bg-elevated px-3 py-1 text-sm font-medium text-text-2">
            {careerFocus}
          </span>
        )}
      </div>
      {showProfileLink && (
        <Button variant="outline" size="sm" className="shrink-0 gap-2" asChild>
          <Link to={`/mentors/${mentorId}`}>
            <User className="h-4 w-4" />
            {t("profile.fullProfile")}
          </Link>
        </Button>
      )}
    </div>
  );

  if (!wrapInCard) {
    const wrapperClassName = className ? `p-6 sm:p-8 ${className}` : "p-6 sm:p-8";
    return <div className={wrapperClassName}>{body}</div>;
  }

  return (
    <Card>
      <CardContent className={className ? `p-6 sm:p-8 ${className}` : "p-6 sm:p-8"}>
        {body}
      </CardContent>
    </Card>
  );
}
